import { useAuth } from './useAuth'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { STALE_TIME } from '../constants/staleTime.js'
import { qk } from '../constants/queryKeys'  // R-audit-2026-09-26 D4.5 收尾:中心化 queryKey

// 模块清单镜像自后端 backend/constants.py 的 MODULE_NAMES
// 顺序与 App.jsx 侧边栏菜单一致(从项目总览 → 系统管理)
// 新增/删除/重命名模块时必须同步更新后端 constants.py
//
// R-2026-08-22 页面「安全质量」重命名:canonical key 由 "safety" → "safety_quality"。
// 老的 "safety" / "quality" 通过 canViewModule 别名解析继续兼容(详见下方 MODULE_ALIASES)。
// 这张表只列 canonical 模块,加 "safety" 会导致 admin 看到重复的"安全质量"勾选项。
const MODULE_NAME_MAP = {
  // R-fix-2026-08-21: 顺序必须与 App.jsx MENU_ITEMS(行 113-130)严格对齐
  // 单一真值源在 App.jsx MENU_ITEMS,这里镜像 — 改了 sidebar 必须同步这里
  // R-audit-2026-09-03: 我的工作排在第一位(用户决策),与 App.jsx / 后端 constants.py 同步
  'my-workbench': '我的工作',
  projects: '项目总览',
  dashboard: '数据看板',
  // R-2026-08-28 v3: 用户决策 - "投资管理" → "投资控制"(全过程投资成本控制 + 项目阶段)
  investment: '投资控制',
  procedures: '项目手续',
  design: '设计管理',
  schedule: '进度管理',
  // R-2026-08-22: 'safety' → 'safety_quality'(页面「安全质量」字面无歧义)。
  // 老 key 'safety' / 'quality' 通过下方 MODULE_ALIASES 桥接。
  safety_quality: '安全质量',
  contract: '合同执行',
  material: '物资管理',
  // svo 现场管理:从 M1 块上移到 9(与 sidebar 一致)
  svo: '现场管理',
  // documents/meeting 互换:sidebar 是 资料→会议,旧表是 会议→资料
  documents: '资料管理',
  meeting: '会议记录',
  persons: '参建人员',
  'project-map': '项目地图',
  'ai': 'AI小助理',
  // (2026-09-01: approvals 随审批功能移除)
  'admin': '系统管理',
  // M1 子模块(大多为 svo 的子 tab,镜像自后端 constants.py)
  qs: '工程量收方',
  risk: '风险登记',
  concealed: '隐蔽工程',
  energization: '送电试运行',
  equipment: '设备进场',
  photo: '项目照片',
  // R145-Round3: 5 个台账 tab 模块镜像自后端 constants.py
  // (之前缺失导致 RequireModule 路由守卫永远返回 false → 403,用户进不去)
  site_contact: '联系单',
  seal_usage: '用章台账',
  document_handover: '资料交接台账',
  work_handover: '工序交接台账',
  work_permit: '工作票台账',
  document_check: '资料检查',  // 2026-09-04 v6:挂在 documents 下自动授权
}

// 模块顺序(用于按菜单顺序排序),从 MODULE_NAME_MAP 的 key 顺序导出
const MODULE_ORDER = Object.keys(MODULE_NAME_MAP)

// 按 MODULE_ORDER 排序模块代码数组(未知 code 排到末尾,便于发现脏数据)
// 用于:org.features 列表展示、用户权限列展示、新建/编辑用户弹窗的权限多选
export const sortByModuleOrder = (codes) => {
  return [...codes].sort((a, b) => {
    const ai = MODULE_ORDER.indexOf(a)
    const bi = MODULE_ORDER.indexOf(b)
    if (ai === -1 && bi === -1) return 0
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

export default function usePermissions() {
  const { user } = useAuth()

  const { data: permData, isLoading: permLoading } = useQuery({
    staleTime: STALE_TIME.DEFAULT,
    // R-Audit-2026-06-22 P0-M: queryKey 加 user.id,避免切账号瞬间 cache 残留显示上家菜单
    queryKey: qk.permissions(user?.id ?? 'anonymous'),
    queryFn: () => axios.get('/api/auth/permissions').then(r => r.data),
    enabled: !!user
  })

  const isSuperAdmin = permData?.is_super_admin || false
  const isPlatformOwner = permData?.is_platform_owner || false
  const modules = permData?.modules || []
  const projectAccess = permData?.projects || []

  // R-audit-2026-08-26: 检测「DB role=admin 但不被识别为 platform_owner」的不一致
  // 这种情况一般是后端 .env.prod PLATFORM_OWNER_USERNAME 没同步,生产反复出过几次
  // (用户硬性要求以后避免)。前端用显眼红条横幅 + console.error 提醒用户联系管理员
  //
  // R-fix-2026-08-26 (P0 false-positive): 必须排除 permLoading=true / !permData 的窗口。
  // /api/auth/me 比 /api/auth/permissions 先返 → 登录瞬间 user.role='admin' 但 permData=undefined
  // → 旧逻辑报 mismatch 红条横幅误闪。后端正常时不应误报。
  const isPlatformOwnerMismatch = !!(
    user && user.role === 'admin' && !isPlatformOwner && permData != null
  )
  if (isPlatformOwnerMismatch && typeof window !== 'undefined' && !window.__platformOwnerMismatchLogged) {
    window.__platformOwnerMismatchLogged = true
    // eslint-disable-next-line no-console
    console.error(
      `[权限不一致] user.role='admin' 但 /api/auth/permissions 返 is_platform_owner=false — 侧栏会空白!`
      + ` 可能原因:后端 .env.prod 缺 PLATFORM_OWNER_USERNAME=${user.username} 或 DB username 与 env 不匹配。请联系管理员。`
    )
  }

  // R-fix-2026-08-20 v5: 保留 hasAnyProject helper(供其他页面使用,如 NoAccessCard / 业务页面),
  // 但菜单/落地页不再用它过滤 — 用户最终诉求是"菜单按 module 权限显示,
  // 没项目的账号可以进但显示 403 卡"。
  const hasAnyProject = isPlatformOwner || isSuperAdmin || projectAccess.length > 0

  // R-2026-08-22 页面「安全质量」重命名后,前端 alias 同步:
  //  - 'quality' / 'safety' 历史 code 桥接到新 canonical 'safety_quality'
  //  - 与后端 constants.py IMPLICIT_MODULE_ALIASES 保持对称
  //  同步范围:
  //  - 老 key (页面曾用 'safety' / 'quality') 通过 MODULE_ALIASES 桥接到 canonical,
  //    老 CSV 数据 / PMP 行 / 调用方传老 key 仍兼容。详见 canViewModule/canEditModule 内 alias resolve。
  //  - 新代码请直接传 'safety_quality'(防回归 CI 会 grep 阻断)
  const MODULE_ALIASES = {
    quality: 'safety_quality',
    safety: 'safety_quality',
  }
  const resolveModuleAlias = (module) => MODULE_ALIASES[module] || module

  const canViewModule = (module) => {
    if (isPlatformOwner) return true
    const canonical = resolveModuleAlias(module)
    const mod = modules.find(m => m.code === canonical)
    return mod?.can_view || false
  }

  const canEditModule = (module) => {
    if (isPlatformOwner) return true
    const canonical = resolveModuleAlias(module)
    const mod = modules.find(m => m.code === canonical)
    return mod?.can_edit || false
  }

  const canAccessProject = (projectId) => {
    if (isPlatformOwner) return true
    // R-Audit-2026-08-04 #E 第三轮:跟 canEditProject 对称,改用项目级字段。
    // 后端 permissions projects[] 已用 target_org_id 过滤,出现在列表里就说明
    // 用户对该项目有访问权(owner / 同 org admin / super_admin / UserProject role)。
    // 不再用全局 isSuperAdmin 兜底(可能跨组织误判)。
    const proj = projectAccess.find(p => p.project_id === Number(projectId))
    if (proj) return true
    return false
  }

  const canEditProject = (projectId) => {
    if (isPlatformOwner) return true
    // R-Audit-2026-08-04 #E:不用全局 isSuperAdmin(可能跨组织误判),
    // 用项目级的 is_org_super_admin / is_org_admin 字段。
    // 后端 verify_project_access 用 target_org_id 校验,
    // 跨组织 super_admin 实际没有编辑权(后端 403),前端不能误给编辑按钮。
    const proj = projectAccess.find(p => p.project_id === Number(projectId))
    if (!proj) return false
    if (proj.is_owner) return true
    if (proj.is_org_super_admin) return true
    if (proj.is_org_admin) return true
    // R-fix-2026-08-22:Phase D (bbf7dac4) 后端把 proj.permissions 从
    // ["schedule.edit", ...] 字符串数组改成 [{module_code, can_view, can_edit}] dict 数组。
    // p.endsWith('.edit') 在 dict 上会抛 TypeError: p.endsWith is not a function
    // (xieshiye 2026-08-22 故障)。兼容两种格式:
    // R-audit-2026-08-30 P1-003: 加 null 守卫,防止 /api/auth/permissions 漏返 permissions 时 TypeError
    return (proj.permissions || []).some(p => {
      if (p && typeof p === 'object') {
        // Phase D dict 格式
        return p.can_edit === true
      }
      if (typeof p === 'string') {
        // 老字符串格式
        return p.endsWith('.edit')
      }
      return false
    })
  }

  const getProjectRole = (projectId) => {
    // R-audit-2026-08-30 P1-002: 删 isSuperAdmin 短路(违反同文件 151-160 '不再用全局 isSuperAdmin' 原则);
    // 跨组织 super_admin 不应拿 'admin' role。isPlatformOwner 由调用方在 UI 层处理(它本来就是 platform 级,与项目无关)。
    const proj = projectAccess.find(p => p.project_id === Number(projectId))
    return proj?.role_name || null
  }

  return {
    isSuperAdmin,
    isPlatformOwner,
    isPlatformOwnerMismatch,
    isLoading: permLoading,
    modules,
    projectAccess,
    canViewModule,
    canEditModule,
    canAccessProject,
    canEditProject,
    getProjectRole,
    hasAnyProject,
  }
}

export { MODULE_NAME_MAP, MODULE_ORDER }